// ChatWidget — a generic, SI-themed chat surface.
//
// It owns only widget state (open/closed + sessionStorage persistence, the
// initial transcript load) and wires the visual components to its three injected
// props: `load`, `send`, `onButtonClick`. It knows nothing about APIs, the
// concierge, navigation, or what a button does — that all lives in whatever
// supplies those props (the "chatContext").
//
// assistant-ui (the heavy dependency) lives in ChatThread, which is lazy-loaded
// on first open, so it stays off the initial bundle until the user opens the chat.
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";

import Button from "Cobalt/Button";
import { Collapse } from "Cobalt/Collapse";
import { ErrorHandler } from "Cobalt/ErrorHandler";
import useAsync from "Common/useAsync";

import StatusIndicator from "./StatusIndicator";
import { decideInitialOpen, readStoredOpenState, writeStoredOpenState } from "./utils";

import type { ChatWidgetProps, WidgetMessage } from "./types";

const ChatThread = lazy(() => import("./ChatThread"));

const loadingIndicator = (
  <div className="flex h-full items-center justify-center">
    <StatusIndicator label="Loading…" />
  </div>
);

export default function ChatWidget({ title = "Concierge", load, send, onButtonClick, onOpenChange }: ChatWidgetProps) {
  // Loads the transcript once on mount. data is null while loading and on failure.
  const { data, isLoading, error } = useAsync<WidgetMessage[]>(load);
  const messages = data ?? [];

  // Optimistic: a returning user who left it open sees it open immediately (and
  // sees the loading state); the post-load decision corrects it once we know
  // whether there's history.
  const [open, setOpen] = useState(() => readStoredOpenState() === "open");
  const userToggled = useRef(false);

  // Apply the initial open/closed decision once the transcript has loaded —
  // unless the user already made a choice while it was loading.
  useEffect(() => {
    if (isLoading || userToggled.current) return;
    const decided = decideInitialOpen({ hasHistory: messages.length > 0, stored: readStoredOpenState() });
    if (decided !== open) {
      setOpen(decided);
      onOpenChange?.(decided, { trigger: "auto" });
    }
  }, [isLoading, messages.length, open, onOpenChange]);

  const toggle = useCallback(() => {
    userToggled.current = true;
    const next = !open;
    writeStoredOpenState(next);
    setOpen(next);
    onOpenChange?.(next, { trigger: "user" });
  }, [open, onOpenChange]);

  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-[380px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-2xl">
      <Button onClick={toggle} aria-expanded={open} transform="none" size="medium" py={3} className="flex w-full items-center justify-between rounded-none">
        <span>{title}</span>
        <span aria-hidden="true">{open ? "▾" : "▴"}</span>
      </Button>
      <Collapse in={open} mountOnEnter unmountOnExit>
        <div className={`flex h-[460px] flex-col ${error ? "justify-center px-4" : ""}`}>
          <ErrorHandler name="ChatWidget" error={error} isError={!isLoading && !!error}>
            {isLoading ? (
              loadingIndicator
            ) : (
              <Suspense fallback={loadingIndicator}>
                <ChatThread initialMessages={messages} send={send} onButtonClick={onButtonClick} />
              </Suspense>
            )}
          </ErrorHandler>
        </div>
      </Collapse>
    </div>
  );
}
